import { ResponsiveImage } from '@order-up/client/vite';
import { formatPrice } from '@order-up/common';
import { resolveItemPrice, toMenuPriceDisplay } from '@order-up/models';
import type { IItem, IMenuGroup } from '@order-up/models';
import React, { useMemo } from 'react';

interface MenuItemProps {
  item: IItem;
  /**
   * ROOT→LEAF chain of menu group ids this item is being displayed under.
   *
   * NOT optional, and not a convenience: `resolveItemPrice` is driven by this
   * chain, and the first group in it with an active pricing strategy wins
   * ("highest branch wins"). A surface that passes nothing here silently prices
   * from `item.basePrice` — which is exactly the defect this prop exists to
   * make impossible. See docs/MENU_PRICING_ARCHITECTURE.md.
   */
  menuGroupPath: number[];
  /** Leaf group id — the group the item actually hangs in on this screen. */
  menuGroupId?: number;
  /** Every group for the restaurant; the resolver walks these by id. */
  allMenuGroups: IMenuGroup[];
}

// Default fallback image for menu items
const DEFAULT_MENU_ITEM_IMAGE = 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=100&h=100&q=80&fit=crop';

export const MenuItem: React.FC<MenuItemProps> = ({
  item,
  menuGroupPath,
  menuGroupId,
  allMenuGroups,
}) => {
  // Resolve through the canonical engine — group pricing and SubgroupReference
  // per-parent overrides both beat the item's own price, and reading
  // `item.basePrice` here would display a number the POS will not charge.
  const price = useMemo(
    () => toMenuPriceDisplay(resolveItemPrice(item, menuGroupPath, menuGroupId, allMenuGroups)),
    [item, menuGroupPath, menuGroupId, allMenuGroups],
  );

  const description = item.customerDescription || item.posDescription;

  // Use imageUrl field (can be OptimizedImage JSON string or plain URL)
  const imageSrc = item.imageUrl || DEFAULT_MENU_ITEM_IMAGE;

  return (
    <div className="menu-item">
      {/* Row 1: Image + Name */}
      <div className="menu-item__image">
        <ResponsiveImage
          src={imageSrc}
          alt={item.name}
          className="menu-item__image-content"
          loading="lazy"
          maxWidth={100}
          fallbackSrc={DEFAULT_MENU_ITEM_IMAGE}
        />
      </div>

      <div className="menu-item__name">{item.name}</div>

      {/* Row 2: Empty space + Pricing (aligned with name column) */}
      <div className="menu-item__spacer"></div>

      <div className="menu-item__pricing">
        {price.kind === 'sizes' && (
          // Size ladder — always two or more; one size collapses to `single`.
          <div className="menu-item__size-prices">
            {price.sizes.map((size) => (
              <div key={size.name} className="menu-item__size-item">
                <span className="menu-item__size-name">{size.name}</span>
                <span className="menu-item__size-price">{formatPrice(size.price)}</span>
              </div>
            ))}
          </div>
        )}

        {price.kind === 'single' && (
          <div className="menu-item__single-price">{formatPrice(price.unitPrice)}</div>
        )}

        {price.kind === 'market' && (
          <div className="menu-item__single-price menu-item__single-price--market">
            Market Price
          </div>
        )}

        {/*
          `unavailable` renders NOTHING. The item is misconfigured (e.g.
          size-based with no sizes), and the previous behavior — printing
          "Market Price" — announced an open-priced item the restaurant never
          configured. An absent price is honest; a borrowed one is not.
        */}
      </div>

      {/* Row 3: Description - full width across both columns */}
      {description && (
        <div className="menu-item__description">{description}</div>
      )}
    </div>
  );
};
